/**
 * One academy's Stripe connection, for a superadmin.
 *
 * Built like `DomainsCard`, because it is the same problem: an asynchronous external system
 * whose truth can only be observed. Same terminal-state set, same staleness threshold, same
 * 5-second refetch that stops once the row settles, same danger banner over a humanised error
 * code, same definition list of each observed fact, same six-event timeline.
 *
 * THE MONEY GOES TO THE ACADEMY. MEREKA TAKES NOTHING.
 * That sentence appears on the card wherever the processing fee is shown, and it is not
 * marketing copy — it is the answer to the one question an operator asks about a fee line,
 * and getting it wrong would be a factual error. The academy is the merchant of record; the
 * funds settle to the academy's own Stripe account; the markup exists so the LEARNER covers
 * the ACADEMY'S Stripe processing charge. Mereka never sees it.
 *
 * IT NAMES THE SECOND SWITCH, AND THERE IS A SECOND SWITCH.
 * Stripe being verified is NOT sufficient for an academy to sell anything: `paid_enrollment`
 * lives in the academy's `THINLMS_FEATURES` site configuration and DEFAULTS OFF, and the
 * pricing gate is `tenant.paid_enrolment_enabled AND payments_state == 'verified'` — both, or
 * nothing is buyable and no price can even be saved. With Stripe verified and that flag off,
 * this card used to read as ready, the course editor said payments were not integrated, and
 * nothing anywhere mentioned a feature flag. The owner had to ask why. It was then fixed by a
 * database edit, which is not a thing an operator should have to do.
 *
 * So the card now distinguishes three states that used to look like two — Stripe not connected,
 * Stripe connected but paid enrolment OFF, and both on — and offers the toggle.
 *
 * THE FLAG IS THE TENANCY APP'S TO WRITE, AND THIS CARD DOES NOT REACH FOR IT.
 * `paid_enrollment` is a declared flag in `config/features.yaml` and it is written through the
 * tenancy app's own `PATCH /api/tenancy/v1/tenants/<slug>`, which validates it against that
 * catalogue and refuses an undeclared flag. This component therefore takes the value and a
 * writer as PROPS from `TenantDetail`, which already owns the tenant document and the mutation
 * that saves it — the same `onSave` the Features card uses. Adding a payments endpoint that
 * wrote a tenancy flag would be a second writer for one field, and the two would diverge on
 * validation first and on cache invalidation immediately after.
 *
 * SECRETS ARE WRITE-ONLY AND THERE IS NO READ-BACK.
 * The form sends a key once. The API returns whether one is present, the last four characters
 * so two keys can be told apart during a rotation, when it was stored, and the Infisical PATH
 * — never the value. There is no "reveal" affordance to add later: an operator who needs the
 * key gets it from Stripe or from Infisical. Same reasoning as `ProvidersCard`, one step
 * further: thinlms does not even hold the value in a column of its own.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, Field, Pill } from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import {
  paymentsApi,
  type PaymentsConfig,
  type PaymentsState,
  type SecretStoreHealth,
} from '../api/payments';
import { promotionsApi } from '../api/promotions';

/** States that will not change on their own, so polling can stop. Mirrors `DomainsCard`. */
const TERMINAL_STATES = new Set<PaymentsState>(['live', 'released', 'not_connected']);
const STALE_AFTER_MS = 2 * 60_000;

/** What a human is told, and what they should do next, per state. Spec §1.5's table. */
const STATE_COPY: Record<PaymentsState, { heading: string; tone: PillTone }> = {
  not_connected: { heading: 'This academy cannot take payments yet.', tone: 'planned' },
  connecting: { heading: 'Checking the keys against Stripe.', tone: 'planned' },
  connected: {
    heading: 'Connected, but Stripe has not enabled charges on this account yet.',
    tone: 'planned',
  },
  verified: { heading: 'Ready to take payments. No payment has been seen yet.', tone: 'active' },
  live: { heading: 'Taking payments.', tone: 'active' },
  error: { heading: 'Action required.', tone: 'suspended' },
  released: { heading: 'Disconnected. Existing orders are unaffected.', tone: 'retired' },
};

type PillTone = 'active' | 'planned' | 'suspended' | 'retired' | 'neutral';

function humanize(value: string | null) {
  if (!value) return 'Pending';
  return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase());
}

function formatTime(value: string | null) {
  if (!value) return 'Not yet';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

/**
 * Three-valued, and it matters.
 *
 * `null` is "Stripe has not told us", which is a different thing from "Stripe said no" — the
 * first means check again, the second means the academy has something to fix. Collapsing them
 * into a single "Not ready" would send an operator to Stripe over a check that has not run.
 */
function fact(value: boolean | null) {
  if (value === null) return 'Not checked yet';
  return value ? 'Yes' : 'No';
}

/** Basis points as a percentage a human reads: 400 -> "4%". */
function percent(bps: number | null) {
  if (bps === null) return null;
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

/** Minor units as an amount, without inventing a currency symbol we were not told. */
function minor(amount: number | null, currency: string) {
  if (amount === null) return null;
  const value = (amount / 100).toFixed(2);
  return currency ? `${currency.toUpperCase()} ${value}` : value;
}

function errorMessage(error: unknown, fallback: string) {
  if (!(error instanceof ApiError)) return fallback;
  const body = error.body as { detail?: string; code?: string } | null;
  if (body?.detail) return body.detail;
  if (error.status === 502) return 'The secret store could not be reached. Nothing was saved.';
  return fallback;
}

export function PaymentsCard({
  slug,
  paidEnrolment,
  onTogglePaidEnrolment,
  togglingPaidEnrolment = false,
  paidEnrolmentIsDefault = false,
}: {
  slug: string;
  /**
   * The academy's `paid_enrollment` flag as the tenancy app reports it, or `undefined` when the
   * tenant document is not available to this card.
   *
   * `undefined` is NOT "off". The card says "could not be read" for it, because "this academy
   * has paid enrolment switched off" and "nobody could tell us" are different facts and only
   * one of them is something an operator should act on by flipping a switch.
   */
  paidEnrolment?: boolean;
  /** Writes the flag through the TENANCY app's own endpoint. See the file header. */
  onTogglePaidEnrolment?: (next: boolean) => void;
  togglingPaidEnrolment?: boolean;
  /** True when the academy has no explicit value and is inheriting the platform default. */
  paidEnrolmentIsDefault?: boolean;
}) {
  const queryClient = useQueryClient();
  const [editingKeys, setEditingKeys] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const query = useQuery({
    queryKey: ['tenant-payments', slug],
    queryFn: () => paymentsApi.get(slug),
    // Poll only while the row can still move on its own, exactly as DomainsCard does. A
    // settled academy must not generate a request every five seconds for as long as the
    // console is open.
    refetchInterval: (q) => (q.state.data && !TERMINAL_STATES.has(q.state.data.state) ? 5_000 : false),
  });

  const settle = (updated: PaymentsConfig) => {
    queryClient.setQueryData(['tenant-payments', slug], updated);
  };

  const submit = useMutation({
    mutationFn: (payload: { secret_key?: string; webhook_secret?: string }) =>
      paymentsApi.submitCredentials(slug, payload),
    onSuccess: (updated) => {
      setEditingKeys(false);
      settle(updated);
    },
  });

  const check = useMutation({
    mutationFn: () => paymentsApi.check(slug),
    onSuccess: settle,
  });

  const disconnect = useMutation({
    mutationFn: () => paymentsApi.disconnect(slug),
    onSuccess: (updated) => {
      setConfirmDisconnect(false);
      settle(updated);
    },
  });

  /**
   * The secret-store connection check, fetched alongside the configuration rather than after
   * the operator tries to save.
   *
   * This is the whole point of the check: the requirement came from a save that failed with a
   * bare 404 (`Folder with path '/thinlms/stripe-test' in environment 'dev' was not found`),
   * and a save that fails mysteriously is worse than one that is refused — the operator's next
   * move is to try the same thing again and then to suspect the key. So the state is on screen
   * BEFORE the paste form is reachable.
   *
   * Not polled. It is a fact about the deployment, not about an academy's Stripe onboarding, so
   * it does not move on its own — it moves when somebody changes configuration, and the
   * refetch below is tied to the actions that could have.
   */
  const storeHealth = useQuery({
    queryKey: ['tenant-payments-secret-store', slug],
    queryFn: () => paymentsApi.secretStoreHealth(slug),
  });

  const provision = useMutation({
    mutationFn: () => paymentsApi.provisionSecretStore(slug),
    onSuccess: (health) => {
      queryClient.setQueryData(['tenant-payments-secret-store', slug], health);
    },
  });

  if (query.isLoading) return <Card title="Payments">Loading payment status…</Card>;

  if (query.isError || !query.data) {
    return (
      <Card title="Payments">
        <Banner tone="danger">
          <strong>Payment status could not be loaded.</strong> Refresh the page to try again.
        </Banner>
      </Card>
    );
  }

  const config = query.data;
  const copy = STATE_COPY[config.state];
  const observedAt = config.observed_at ? new Date(config.observed_at).valueOf() : null;
  const stale =
    config.desired_state === 'active' &&
    !TERMINAL_STATES.has(config.state) &&
    (!observedAt || Number.isNaN(observedAt) || Date.now() - observedAt > STALE_AFTER_MS);

  const connected = config.state !== 'not_connected' && config.state !== 'released';

  // `undefined` while loading is "not yet known", never "fine" — the whole reason this check
  // exists is that an unknown store is the one most likely to fail a save for a reason nobody
  // can see afterwards.
  //
  // The button stays VISIBLE and goes disabled rather than disappearing. AC-001 requires that
  // an unconfigured academy is offered "Add Stripe keys", and a primary action that vanishes
  // reads as a broken page rather than as a blocked one — the operator starts looking for the
  // button instead of reading the sentence that says why it is inert.
  const health = storeHealth.data;
  const canSaveAKey = health?.can_save_a_key === true;

  return (
    <Card
      title="Payments"
      action={
        <div className="console__form-actions" style={{ margin: 0 }}>
          {connected && (
            <Button busy={check.isPending} onClick={() => check.mutate()}>
              Check now
            </Button>
          )}
          {!editingKeys && (
            <Button
              variant="primary"
              disabled={!canSaveAKey}
              onClick={() => setEditingKeys(true)}
            >
              {config.has_secret ? 'Replace keys' : 'Add Stripe keys'}
            </Button>
          )}
        </div>
      }
    >
      <SecretStoreStatus
        health={health}
        loading={storeHealth.isLoading}
        failed={storeHealth.isError}
        provisioning={provision.isPending}
        provisionFailed={provision.isError}
        onProvision={() => provision.mutate()}
        onRecheck={() => storeHealth.refetch()}
      />

      <div className="console__payments-state">
        <Pill tone={copy.tone}>{humanize(config.state)}</Pill>
        <p>
          {config.state === 'error' && config.error_sentence ? config.error_sentence : copy.heading}
        </p>
      </div>

      <PaidEnrolmentSwitch
        stripeVerified={config.state === 'verified' || config.state === 'live'}
        value={paidEnrolment}
        isDefault={paidEnrolmentIsDefault}
        busy={togglingPaidEnrolment}
        onToggle={onTogglePaidEnrolment}
      />

      {/* THE THIRD AND FOURTH SWITCHES, and they had no control at all until now:
          `TenantPromotionSettings.promo_codes_enabled` defaults false, the mint path refuses on
          it, and the only way it was ever turned on was a hand-edit from a shell. They are read
          and written through the PROMOTIONS app's own endpoint rather than through this card's,
          because that app owns the row — see the component's own note for why it fetches
          instead of taking props like the toggle above it. */}
      <PromotionSwitches slug={slug} />

      {/* The state-specific "what next" sentence. */}
      {config.state === 'not_connected' && (
        <p className="console__hint">
          Add this academy&rsquo;s own Stripe secret key and webhook signing secret. Learners
          pay the academy directly and the money settles to the academy&rsquo;s Stripe account.
        </p>
      )}
      {config.state === 'connecting' && (
        <p className="console__hint">
          Keys submitted {formatTime(config.secret_written_at)}. Checking them against Stripe.
        </p>
      )}
      {config.state === 'connected' && (
        <MissingCapabilities config={config} />
      )}
      {config.state === 'released' && (
        <p className="console__hint">
          The keys were deleted from the secret store and the Stripe notification endpoint was
          removed. Orders already paid are unaffected and still grant access.
        </p>
      )}

      {stale && (
        <Banner tone="warning">
          <strong>Status may be stale.</strong> The last successful check was{' '}
          {formatTime(config.observed_at)}.
        </Banner>
      )}

      {config.last_error_code && (
        <Banner tone={config.state === 'error' ? 'danger' : 'warning'}>
          <strong>{humanize(config.last_error_code)}.</strong>{' '}
          {config.error_sentence || config.last_error_message}
          {config.last_error_code === 'secret_ref_unresolvable' && config.secret_ref && (
            <>
              {' '}
              Expected at <code>{config.secret_ref}</code>.
            </>
          )}
          <ErrorAction action={config.error_action} config={config} />
        </Banner>
      )}

      {check.isError && (
        <Banner tone="danger">
          <strong>The check did not run.</strong>{' '}
          {errorMessage(check.error, 'Try again in a moment.')}
        </Banner>
      )}

      {editingKeys && (
        <CredentialForm
          hasSecret={config.has_secret}
          lastFour={config.secret_last_four}
          busy={submit.isPending}
          error={submit.isError ? submit.error : null}
          onCancel={() => {
            setEditingKeys(false);
            submit.reset();
          }}
          onSubmit={(payload) => submit.mutate(payload)}
        />
      )}

      <dl className="console__domain-facts">
        <dt>Stripe account</dt>
        <dd className="mono">{config.stripe_account_id || 'Not connected'}</dd>
        <dt>Key stored</dt>
        <dd>
          {config.has_secret ? (
            <>
              Yes, ending <code>{config.secret_last_four || '····'}</code>, set{' '}
              {formatTime(config.secret_written_at)}
            </>
          ) : (
            'No'
          )}
        </dd>
        <dt>Key location</dt>
        <dd className="mono">{config.secret_ref || '—'}</dd>
        <dt>Webhook signing secret</dt>
        <dd>{config.has_webhook_secret ? 'Stored' : 'Not stored'}</dd>
        <dt>Stripe onboarding finished</dt>
        <dd>{fact(config.details_submitted)}</dd>
        <dt>Can accept charges</dt>
        <dd>{fact(config.charges_enabled)}</dd>
        <dt>Can receive payouts</dt>
        <dd>{fact(config.payouts_enabled)}</dd>
        <dt>Key can create products</dt>
        <dd>{fact(config.write_probe_ok)}</dd>
        <dt>Country</dt>
        <dd>{config.account_country || 'Unknown'}</dd>
        <dt>Settlement currency</dt>
        <dd>{config.default_currency ? config.default_currency.toUpperCase() : 'Unknown'}</dd>
        <dt>Payment methods</dt>
        <dd>
          {config.payment_method_types.length
            ? config.payment_method_types.join(', ')
            : 'None available yet'}
        </dd>
        <dt>Mode</dt>
        <dd>{config.livemode === null ? 'Unknown' : config.livemode ? 'Live' : 'Test'}</dd>
        <dt>Notification endpoint</dt>
        <dd>{fact(config.webhook_endpoint_ok)}</dd>
        <dt>Last payment notification</dt>
        <dd>{formatTime(config.last_event_at)}</dd>
        <dt>Last checked</dt>
        <dd>{formatTime(config.observed_at)}</dd>
        <dt>Next automatic check</dt>
        <dd>{formatTime(config.next_retry_at)}</dd>
        <dt>Failed checks</dt>
        <dd>{config.retry_count}</dd>
      </dl>

      <MarkupSummary config={config} />

      {connected && (
        <div className="console__form-actions">
          {!confirmDisconnect ? (
            <Button variant="ghost" onClick={() => setConfirmDisconnect(true)}>
              Disconnect Stripe
            </Button>
          ) : (
            <>
              <span className="console__hint">
                This deletes both keys from the secret store and removes the Stripe notification
                endpoint. Orders already paid still grant access. Continue?
              </span>
              <Button variant="primary" busy={disconnect.isPending} onClick={() => disconnect.mutate()}>
                Yes, disconnect
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDisconnect(false)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      )}
      {disconnect.isError && (
        <Banner tone="danger">
          <strong>The academy was not disconnected.</strong>{' '}
          {errorMessage(disconnect.error, 'Try again in a moment.')}
        </Banner>
      )}

      <EventTimeline events={config.events} />

      <p className="console__inherited">
        Contracts checked against {config.diagnostics.openedx_release} on{' '}
        {config.diagnostics.contracts_measured_on}.
      </p>
    </Card>
  );
}

/**
 * Can a key be saved at all, shown BEFORE the form that would save one.
 *
 * This is the connection check the owner asked for by name, and it is at the top of the card
 * rather than tucked in a diagnostics drawer because of where the requirement came from: a save
 * against the real server failed with
 *
 *     Folder with path '/thinlms/stripe-test' in environment 'dev' was not found
 *
 * and it surfaced as a bare 404. An operator who is told "that folder has not been created yet"
 * and given a button fixes it in one click; an operator shown a 404 tries the same paste again
 * and then starts doubting the key. So: named state, a sentence, a next action, and the address
 * being targeted.
 *
 * Nothing here can show a value. Every field the endpoint returns is a state name, a boolean, a
 * number or an ADDRESS — the environment, the project, the path — and a backend test asserts no
 * response from it contains anything secret-shaped. A path is not a credential, and an operator
 * who cannot see the address cannot check it, which is the most common real problem.
 *
 * When the state is healthy this collapses to one quiet line, because a green check that shouts
 * teaches people to skip reading it.
 */
function SecretStoreStatus({
  health,
  loading,
  failed,
  provisioning,
  provisionFailed,
  onProvision,
  onRecheck,
}: {
  health: SecretStoreHealth | undefined;
  loading: boolean;
  failed: boolean;
  provisioning: boolean;
  provisionFailed: boolean;
  onProvision: () => void;
  onRecheck: () => void;
}) {
  if (loading) {
    return <p className="console__hint">Checking the secret store…</p>;
  }

  if (failed || !health) {
    // "Unknown" and not "fine". The paste button is hidden while this is the case, because an
    // unknown store is the state in which a save is most likely to fail for a reason nobody can
    // see afterwards.
    return (
      <Banner tone="warning">
        <strong>The secret store could not be checked.</strong> Adding a Stripe key is held
        until it answers — a save against a store in an unknown state leaves nothing stored and
        no explanation of why.{' '}
        <Button variant="ghost" onClick={onRecheck}>
          Check again
        </Button>
      </Banner>
    );
  }

  if (health.state === 'healthy') {
    return (
      <p className="console__hint">
        Keys for this academy are stored in the secret store at <code>{health.path}</code> (
        {health.environment}).{' '}
        {health.shared_invalidation
          ? 'A saved key takes effect immediately on every server.'
          : `A saved key takes effect immediately here and within ${health.convergence_seconds} seconds on the other servers — no restart or deploy is needed either way.`}
      </p>
    );
  }

  return (
    <Banner tone={health.state === 'in_memory_only' ? 'warning' : 'danger'}>
      <strong>{health.sentence}</strong> <em>{health.next_action_sentence}</em>
      <dl className="console__domain-facts">
        <dt>Where keys would go</dt>
        <dd className="mono">{health.path}</dd>
        <dt>Secret store environment</dt>
        <dd className="mono">{health.environment || 'Not set'}</dd>
        <dt>Secret store project</dt>
        <dd className="mono">{health.project || 'Not set'}</dd>
        <dt>Condition</dt>
        <dd className="mono">{health.state}</dd>
      </dl>
      {health.breaker !== 'closed' && (
        <p className="console__hint">
          The secret store is being left alone to recover after repeated failures, so this page
          is not retrying it on every load. Academies already selling are unaffected until their
          cached key expires.
        </p>
      )}
      <div className="console__form-actions">
        {/*
          Offered only for the one state the backend can actually fix. A "Create folder" button
          on `identity_invalid` would do nothing and would teach an operator that the buttons on
          this card are decorative.
        */}
        {health.next_action === 'create_path' && (
          <Button variant="primary" busy={provisioning} onClick={onProvision}>
            Create folder
          </Button>
        )}
        <Button variant="ghost" onClick={onRecheck}>
          Check again
        </Button>
      </div>
      {provisionFailed && (
        <p className="console__hint">
          The folder was not created. Use <strong>Check again</strong> — the condition above will
          say what changed.
        </p>
      )}
    </Banner>
  );
}

/**
 * WHICH of the three Stripe flags is false.
 *
 * §1.5 requires naming it, because "Stripe has not enabled charges" describes three different
 * problems with three different owners: unfinished onboarding is the academy's to complete,
 * charges disabled is usually Stripe reviewing them, and payouts disabled is a bank-detail
 * problem. One sentence for all three sends a human to a support queue.
 */
function MissingCapabilities({ config }: { config: PaymentsConfig }) {
  const missing = [
    config.details_submitted !== true && 'Stripe onboarding is not finished',
    config.charges_enabled !== true && 'charges are not enabled',
    config.payouts_enabled !== true && 'payouts are not enabled',
  ].filter(Boolean) as string[];

  return (
    <p className="console__hint">
      {missing.length ? `On this Stripe account, ${missing.join(', and ')}.` : ''} The academy
      completes this in their own Stripe dashboard; use <strong>Check now</strong> afterwards.
    </p>
  );
}

/**
 * The processing fee, its source, and who receives it.
 *
 * The "who receives it" line is required wherever the fee appears (§1.5). It is the one
 * question an operator asks about a fee line, and any copy that could read as "Mereka's cut"
 * would be factually wrong: the markup is collected on the academy's own Stripe account and
 * settles to the academy.
 */
function MarkupSummary({ config }: { config: PaymentsConfig }) {
  const rate = percent(config.markup_bps);
  const fixed = minor(config.markup_fixed_minor, config.default_currency);
  const source =
    config.markup_source === 'tenant' ? 'set for this academy' : 'the platform default';

  return (
    <div className="console__dns-instruction">
      <div>
        <span>Payment processing fee added to learner prices</span>
        {rate || fixed ? (
          <code>
            {[rate, fixed].filter(Boolean).join(' + ')} — {source}
          </code>
        ) : (
          <code>Using {source}</code>
        )}
        <p className="console__hint">
          Money goes directly to this academy&rsquo;s Stripe account. Mereka does not take a
          share. This fee covers the academy&rsquo;s own Stripe processing charge, so the
          learner pays it rather than the academy absorbing it.
        </p>
      </div>
    </div>
  );
}

/** The action §1.5 pairs with each error code, rendered as a sentence rather than a button. */
function ErrorAction({ action, config }: { action: string; config: PaymentsConfig }) {
  const sentences: Record<string, string> = {
    retry_submit: 'Submit the keys again. If it keeps failing, the secret store is unavailable.',
    resubmit_keys: 'Submit the keys again, or fix the secret store, then use Check now.',
    replace_keys: 'Use Replace keys with a key from this academy’s Stripe dashboard.',
    contact_stripe: 'The academy needs to resolve this with Stripe directly.',
    open_stripe_onboarding: 'The academy finishes onboarding in their own Stripe dashboard.',
    check_key_permissions:
      'The key is scoped too narrowly. A restricted key needs write access to Products and Prices, and read access to Coupons.',
    check_now: 'Use Check now. If it keeps failing, escalate.',
    escalate: 'This is unexpected. Please escalate it.',
  };
  const sentence = sentences[action];
  if (!sentence) return null;
  return (
    <>
      {' '}
      <em>{sentence}</em>
      {action === 'contact_stripe' && config.stripe_account_id ? (
        <>
          {' '}
          Account <code>{config.stripe_account_id}</code>.
        </>
      ) : null}
    </>
  );
}

/**
 * The paste-once credential form.
 *
 * Both inputs are `type="password"`, `autoComplete="off"`, and neither is ever populated from
 * the server — there is nothing to populate them from. Leaving a field blank keeps the
 * credential that is already stored, which is what makes rotating one without touching the
 * other possible, and what stops a partial edit silently wiping a working key.
 */
function CredentialForm({
  hasSecret,
  lastFour,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  hasSecret: boolean;
  lastFour: string;
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (payload: { secret_key?: string; webhook_secret?: string }) => void;
}) {
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [localError, setLocalError] = useState('');

  // The same prefix check the backend performs, run here too so the common operator slip —
  // the two values in each other's boxes — is caught before a round trip. The backend check is
  // the authority; this one only saves a second.
  const keyLooksWrong = secretKey && !/^(sk|rk)_/.test(secretKey);
  const webhookLooksWrong = webhookSecret && !/^whsec_/.test(webhookSecret);

  return (
    <form
      className="console__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!secretKey && !webhookSecret) {
          setLocalError('Enter at least one of the two values.');
          return;
        }
        if (keyLooksWrong || webhookLooksWrong) return;
        setLocalError('');
        onSubmit({
          ...(secretKey ? { secret_key: secretKey } : {}),
          ...(webhookSecret ? { webhook_secret: webhookSecret } : {}),
        });
        // Cleared immediately. The value has left for the server and must not sit in a React
        // state tree, a re-render, or a browser autofill cache.
        setSecretKey('');
        setWebhookSecret('');
      }}
    >
      <Field
        label="Stripe secret key"
        name="stripe-secret-key"
        type="password"
        autoComplete="off"
        placeholder={hasSecret ? `Currently set, ending ${lastFour}` : 'sk_live_… or rk_live_…'}
        value={secretKey}
        error={keyLooksWrong ? 'A secret key starts with sk_ or rk_. A whsec_ value belongs in the field below.' : ''}
        hint="Stored write-only in the secret store and never shown again. Leave blank to keep the current key."
        disabled={busy}
        onChange={(event) => setSecretKey(event.target.value)}
      />
      <Field
        label="Webhook signing secret"
        name="stripe-webhook-secret"
        type="password"
        autoComplete="off"
        placeholder="whsec_…"
        value={webhookSecret}
        error={webhookLooksWrong ? 'A webhook signing secret starts with whsec_. A secret key belongs in the field above.' : ''}
        hint="From this academy's Stripe webhook endpoint. Rotates independently of the secret key."
        disabled={busy}
        onChange={(event) => setWebhookSecret(event.target.value)}
      />

      {localError && <Banner tone="warning">{localError}</Banner>}
      {error !== null && (
        <Banner tone="danger">
          <strong>The keys were not saved.</strong>{' '}
          {errorMessage(error, 'Nothing was stored, so it is safe to try again.')}
        </Banner>
      )}

      <div className="console__form-actions">
        <Button type="submit" variant="primary" busy={busy}>
          Save and verify
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The last six events, newest first — the same window `DomainsCard` shows. */
function EventTimeline({ events }: { events: PaymentsConfig['events'] }) {
  const recent = [...events]
    .sort((left, right) => new Date(right.created_at).valueOf() - new Date(left.created_at).valueOf())
    .slice(0, 6);

  if (!recent.length) return null;

  return (
    <div className="console__domain-activity">
      <h4>Recent activity</h4>
      <ol>
        {recent.map((event) => (
          <li key={event.id}>
            <time dateTime={event.created_at}>{formatTime(event.created_at)}</time>
            <span>
              <strong>{humanize(event.event_type)}</strong> {event.message}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * THE SECOND SWITCH, SAID OUT LOUD.
 *
 * `paid_enrollment` is a per-academy feature flag in `THINLMS_FEATURES` and it DEFAULTS OFF.
 * The pricing gate is `gate_holds = tenant.paid_enrolment_enabled AND payments_state ==
 * 'verified'` — both, always — so with Stripe verified and this flag off:
 *
 *   * no author can save a price at all (409, "Paid enrolment is switched off in this
 *     academy's settings"),
 *   * nothing is buyable,
 *   * and this card used to say "Ready to take payments" while the course editor said payments
 *     were not integrated, with no mention anywhere that a flag existed.
 *
 * Confirmed on dev: setting the flag in the database made the API report `paid_enrollment:
 * true` immediately. A configuration switch whose only interface is a database edit is not a
 * configuration switch, it is a secret.
 *
 * THREE STATES, NOT TWO. "Stripe is not connected" and "Stripe is connected and this academy is
 * not selling" need different sentences because they need different actions from different
 * people: the first is a key, the second is one click. Collapsing them is what made the owner
 * ask why a verified academy could not sell anything.
 *
 * `undefined` IS ITS OWN ANSWER and is never rendered as "off". The card says it could not be
 * read and offers no toggle, because a switch that flips a value nobody could read is a switch
 * that writes a guess over whatever is really there.
 */
function PaidEnrolmentSwitch({
  stripeVerified,
  value,
  isDefault,
  busy,
  onToggle,
}: {
  stripeVerified: boolean;
  value: boolean | undefined;
  isDefault: boolean;
  busy: boolean;
  onToggle?: (next: boolean) => void;
}) {
  if (value === undefined) {
    return (
      <Banner tone="warning">
        <strong>Paid enrolment: could not be read.</strong> This academy&rsquo;s feature flags
        did not load, so whether it may sell courses is unknown. Nothing has been changed.
        Reload the page.
      </Banner>
    );
  }

  const on = value === true;

  return (
    <div className="console__payments-state">
      <Pill tone={on ? 'active' : 'planned'}>
        {on ? 'Paid enrolment on' : 'Paid enrolment off'}
      </Pill>
      <p>
        {on
          ? stripeVerified
            ? 'This academy can price and sell courses.'
            : 'Paid enrolment is on, but Stripe is not verified yet, so nothing can be sold until it is. Both are required.'
          : stripeVerified
            ? 'Stripe is ready and this academy still cannot sell anything: paid enrolment is switched off, so authors are refused when they try to set a price and no course is buyable. Both are required.'
            : 'Paid enrolment is off and Stripe is not connected. Both are required before this academy can sell anything.'}
      </p>
      {isDefault && (
        <p className="console__hint">
          Inherited from the platform default in <code>config/features.yaml</code>, where{' '}
          <code>paid_enrollment</code> is <code>false</code>. Nothing has been set for this
          academy specifically.
        </p>
      )}
      {onToggle ? (
        <div className="console__form-actions" style={{ margin: 0 }}>
          <Button
            variant={on ? undefined : 'primary'}
            busy={busy}
            onClick={() => onToggle(!on)}
          >
            {on ? 'Switch paid enrolment off' : 'Switch paid enrolment on'}
          </Button>
        </div>
      ) : (
        <p className="console__hint">
          This is the <code>paid_enrollment</code> feature flag. Change it in the Features card
          above.
        </p>
      )}
    </div>
  );
}


/**
 * DISCOUNT CODES AND ACCESS CODES — the two phase switches, beside the paid-enrolment one.
 *
 * WHY IT FETCHES ITS OWN VALUE while `PaidEnrolmentSwitch` takes props. `paid_enrollment` is a
 * `config/features.yaml` flag on the tenant document, and `TenantDetail` already holds that
 * document and the mutation that saves it — so passing props there avoids a SECOND writer for
 * one field. These two are columns in `openedx_thinlms_promotions`' own table and nothing else
 * in the console holds them, so the one writer is that app's own endpoint and this is the only
 * reader. The rule being followed is "one writer, in the app that owns the row", not "always
 * take props".
 *
 * FOUR STATES, AND THE FOURTH IS THE ONE THAT USED TO BE MISSING:
 *
 *   not yet read     "could not be read", never "off". The whole reason the toggle exists is
 *                    that a switch nobody can see gets set from a shell.
 *   never set        `explicitly_set: false` — a fail-closed default, not somebody's decision.
 *   off / on         somebody's decision, with the sentence for what that means today.
 *   on, Stripe not   the phase is open and minting still refuses, in Stripe's own words. Both
 *   ready            are shown, because "switched on" is not "working".
 *
 * THE SWITCH IS OFFERED EVEN WHEN STRIPE IS NOT READY, deliberately. The phase and the Stripe
 * connection are two independent facts and the rollout is allowed to open the phase first; the
 * server's readiness sentence says what will happen if somebody tries to mint before the
 * account is connected. Hiding the control would leave an operator with no way to prepare an
 * academy, which is the state this whole card exists to have fixed once.
 */
export function PromotionSwitches({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const [noOp, setNoOp] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['tenant-promotions', slug],
    queryFn: () => promotionsApi.get(slug),
    // A refusal here is a statement about this operator or this academy, not a transient fault.
    retry: false,
  });

  const write = useMutation({
    mutationFn: (switches: { promo_codes_enabled?: boolean; access_codes_enabled?: boolean }) =>
      promotionsApi.setSwitches(slug, switches),
    onSuccess: (updated) => {
      // The server rebuilt the whole record to answer, so it is written in rather than
      // refetched — the same reason `settle()` above does it for the payments row.
      queryClient.setQueryData(['tenant-promotions', slug], updated);
      setNoOp(
        updated.changed
          ? null
          : 'That was already the setting, so nothing was changed. This is not a failure.',
      );
    },
  });

  if (query.isLoading) {
    return <p className="console__hint">Reading this academy&rsquo;s promotion phases…</p>;
  }

  if (query.isError || !query.data) {
    return (
      <Banner tone="warning">
        <strong>Discount and access codes: could not be read.</strong>{' '}
        {errorMessage(
          query.error,
          'The promotions app did not answer, so whether this academy may issue codes is unknown.',
        )}{' '}
        Nothing has been changed.
      </Banner>
    );
  }

  const switches = query.data;

  return (
    <div className="console__payments-state">
      <Pill tone={switches.promo_codes_enabled ? 'active' : 'planned'}>
        {switches.promo_codes_enabled ? 'Discount codes on' : 'Discount codes off'}
      </Pill>
      <p>
        {switches.promo_codes_enabled
          ? 'This academy can create Stripe discount codes. Learners enter them in Stripe’s own field on the hosted checkout page.'
          : 'This academy cannot create discount codes. Authors are refused when they try, and no code can be minted.'}
      </p>

      {/* THE PHASE BEING OPEN IS NOT THE SAME AS IT WORKING, and the server owns that sentence
          because it read the Stripe account to write it. */}
      {switches.promo_codes_enabled && !switches.stripe_ready.ready && (
        <Banner tone="warning">
          <strong>Switched on, and not usable yet.</strong> {switches.stripe_ready.message}
        </Banner>
      )}

      <Pill tone={switches.access_codes_enabled ? 'active' : 'planned'}>
        {switches.access_codes_enabled ? 'Access codes on' : 'Access codes off'}
      </Pill>
      <p>
        {switches.access_codes_enabled
          ? 'This academy can issue access codes, which grant an item free and make no Stripe call at all — so they keep working while Stripe is unreachable.'
          : 'This academy cannot issue access codes. A 100%-off promotion is an access code rather than a Stripe coupon, so this is the switch that governs one.'}
      </p>

      {!switches.explicitly_set && (
        <p className="console__hint">
          Neither has ever been set for this academy, so both are the platform&rsquo;s
          fail-closed default. Nothing has been decided about this academy specifically.
        </p>
      )}

      {noOp && <p className="console__hint">{noOp}</p>}
      {write.isError && (
        <Banner tone="danger">
          <strong>That switch was not changed.</strong>{' '}
          {errorMessage(write.error, 'The promotions app refused the change.')}
        </Banner>
      )}

      <div className="console__form-actions" style={{ margin: 0 }}>
        <Button
          variant={switches.promo_codes_enabled ? undefined : 'primary'}
          busy={write.isPending}
          onClick={() => {
            setNoOp(null);
            write.mutate({ promo_codes_enabled: !switches.promo_codes_enabled });
          }}
        >
          {switches.promo_codes_enabled
            ? 'Switch discount codes off'
            : 'Switch discount codes on'}
        </Button>
        <Button
          busy={write.isPending}
          onClick={() => {
            setNoOp(null);
            write.mutate({ access_codes_enabled: !switches.access_codes_enabled });
          }}
        >
          {switches.access_codes_enabled ? 'Switch access codes off' : 'Switch access codes on'}
        </Button>
      </div>
    </div>
  );
}
